/**
 * Email login localization fallback strings.
 *
 * These keys must be added to the remote L10N management system to appear in production.
 * This file provides fallback values so the email login flow works before L10N keys are provisioned.
 *
 * Keys to add to L10N system:
 *   titles.loginWithEmail           = "Log in with email"
 *   titles.loginWithPhone           = "Log in with phone"
 *   titles.enter_your_email         = "Enter your email address"
 *   titles.email_activation_message = "A confirmation code will be sent to your email"
 *   titles.codeHasBeenSentToEmail   = "Code has been sent to {email}"
 *   titles.resendAvailableIn        = "Re-send available in {seconds} seconds"
 *   placeholders.email              = "Email"
 *   error.invalid_email_format      = "Enter a valid email address"
 *   error.email_login_unavailable   = "Email login is currently unavailable"
 *   error.code_expired_request_new  = "Your code has expired. Please request a new one."
 *   screenTitles.EmailSignIn        = "Email login"
 */

import I18n from 'i18n-js';

const EMAIL_LOGIN_STRINGS = {
    ru: {
        screenTitles: {
            EmailSignIn: 'Вход по Email',
        },
        titles: {
            loginWithEmail: 'Войти через email',
            loginWithPhone: 'Войти через телефон',
            enter_your_email: 'Введите ваш email',
            email_activation_message: 'Код подтверждения будет отправлен на ваш email',
            codeHasBeenSentToEmail: 'Код отправлен на {email}',
            resendAvailableIn: 'Повторная отправка через {seconds} сек.',
            enter_code_email: 'Введите код из Email',
            sendAgainEmail: 'Отправить код ещё раз',
        },
        placeholders: {
            email: 'Email',
        },
        error: {
            invalid_email_format: 'Введите корректный email',
            email_login_unavailable: 'Вход через email временно недоступен',
            code_expired_request_new: 'Срок действия кода истёк. Запросите новый код.',
        },
    },
    en: {
        screenTitles: {
            EmailSignIn: 'Email login',
        },
        titles: {
            loginWithEmail: 'Log in with email',
            loginWithPhone: 'Log in with phone',
            enter_your_email: 'Enter your email',
            email_activation_message: 'A confirmation code will be sent to your email',
            codeHasBeenSentToEmail: 'Code has been sent to {email}',
            resendAvailableIn: 'Resend available in {seconds} sec.',
            enter_code_email: 'Enter code from Email',
            sendAgainEmail: 'Send code again',
        },
        placeholders: {
            email: 'Email',
        },
        error: {
            invalid_email_format: 'Enter a valid email',
            email_login_unavailable: 'Email login is temporarily unavailable',
            code_expired_request_new: 'Code has expired. Please request a new one.',
        },
    },
};

const mergeDeep = (target, source) => {
    const result = { ...target };
    Object.keys(source).forEach((key) => {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            result[key] = mergeDeep(result[key] || {}, source[key]);
        } else if (!(key in (result || {}))) {
            result[key] = source[key];
        }
    });
    return result;
};

/**
 * Injects email login localization fallback strings into the active i18n locale.
 * Only fills in keys that are not yet present (does not override existing L10N values).
 * Call this after setLocale() during app initialization.
 */
export const applyEmailLoginFallbackStrings = () => {
    const locale = I18n.locale || 'ru';
    const fallback = EMAIL_LOGIN_STRINGS[locale] || EMAIL_LOGIN_STRINGS.ru;
    const existing = I18n.translations[locale] || {};
    I18n.translations[locale] = mergeDeep(existing, fallback);
};
